import axios from 'axios';

/*
|--------------------------------------------------------------------------
| API URL
|--------------------------------------------------------------------------
| Local:
|   React -> /api -> backend proxy
|
| Production:
|   REACT_APP_API_URL=https://your-server.onrender.com
|
| Phone / LAN:
|   http://YOUR-PC-IP:5000/api
|--------------------------------------------------------------------------
*/

const normalizeBaseUrl = (url) => {
  if (!url) return '';

  return url
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api$/, '');
};

const getServerUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // Production / custom API URL
  if (envUrl) {
    return normalizeBaseUrl(envUrl);
  }

  const hostname = window.location.hostname;

  // Local development
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  ) {
    return '';
  }

  // Phone / another device on same Wi-Fi
  return `http://${hostname}:5000`;
};

const SERVER_URL = getServerUrl();

const getBaseURL = () => {
  // Local development uses CRA proxy
  if (!SERVER_URL) {
    return '/api';
  }

  return `${SERVER_URL}/api`;
};


/*
|--------------------------------------------------------------------------
| Socket URL
|--------------------------------------------------------------------------
*/

export const SOCKET_URL =
  SERVER_URL || 'https://nexa-message-api.onrender.com';


/*
|--------------------------------------------------------------------------
| Axios instance
|--------------------------------------------------------------------------
*/

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});


/*
|--------------------------------------------------------------------------
| Request Interceptor
|--------------------------------------------------------------------------
*/

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nexus_token');

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


/*
|--------------------------------------------------------------------------
| Response Interceptor
|--------------------------------------------------------------------------
*/

api.interceptors.response.use(
  (response) => {
    return response;
  },

  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      const currentPath = window.location.pathname;

      /*
       * Do not redirect while already inside authentication pages.
       */
      const isAuthPage =
        currentPath === '/' ||
        currentPath === '/login' ||
        currentPath === '/register';

      /*
       * Do not automatically redirect failed login/register requests.
       * Otherwise a wrong password could unexpectedly send the user
       * somewhere else.
       */
      const requestUrl = error.config?.url || '';

      const isAuthRequest =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/send-otp') ||
        requestUrl.includes('/auth/verify-otp') ||
        requestUrl.includes('/auth/complete-profile');

      if (!isAuthRequest) {
        localStorage.removeItem('nexus_token');

        /*
         * Dispatch an event so AuthContext can also clear its user.
         */
        window.dispatchEvent(new Event('auth:logout'));

        /*
         * After authentication expires:
         *
         * / -> LandingPage
         *
         * Then user can choose:
         * Login
         * Register
         */
        if (!isAuthPage) {
          window.location.replace('/');
        }
      }
    }

    return Promise.reject(error);
  }
);


export default api;


/*
|--------------------------------------------------------------------------
| AUTH API
|--------------------------------------------------------------------------
*/

export const authAPI = {

  sendOTP: (phone) =>
    api.post('/auth/send-otp', {
      phone,
    }),

  verifyOTP: (phone, otp) =>
    api.post('/auth/verify-otp', {
      phone,
      otp,
    }),

  completeProfile: (data) =>
    api.post('/auth/complete-profile', data),

  login: (email, password) =>
    api.post('/auth/login', {
      email,
      password,
    }),

  register: (data) =>
    api.post('/auth/register', data),

  me: () =>
    api.get('/auth/me'),

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      /*
       * Even if the backend logout request fails,
       * the local session must still be removed.
       */
      console.warn(
        'Logout API request failed:',
        error?.response?.data || error.message
      );
    } finally {
      localStorage.removeItem('nexus_token');

      window.dispatchEvent(
        new Event('auth:logout')
      );
    }
  },
};


/*
|--------------------------------------------------------------------------
| USERS API
|--------------------------------------------------------------------------
*/

export const usersAPI = {

  getAll: () =>
    api.get('/users'),

  search: (q) =>
    api.get(
      `/users/search?q=${encodeURIComponent(q)}`
    ),

  getById: (id) =>
    api.get(`/users/${id}`),

  updateProfile: (data) =>
    api.put('/users/profile', data),

  follow: (id) =>
    api.post(`/users/${id}/follow`),

  unfollow: (id) =>
    api.delete(`/users/${id}/follow`),

  block: (id) =>
    api.post(`/users/${id}/block`),

  unblock: (id) =>
    api.post(`/users/${id}/unblock`),

  addContact: (id) =>
    api.post(`/users/${id}/contact`),

  getContacts: () =>
    api.get('/users/me/contacts'),
};


/*
|--------------------------------------------------------------------------
| MESSAGES API
|--------------------------------------------------------------------------
*/

export const messagesAPI = {

  getDMs: (userId, page = 1) =>
    api.get(
      `/messages/dm/${userId}?page=${page}`
    ),

  sendDM: (userId, data) =>
    api.post(
      `/messages/dm/${userId}`,
      data
    ),

  getGroupMessages: (groupId) =>
    api.get(
      `/messages/group/${groupId}`
    ),

  sendGroupMessage: (groupId, data) =>
    api.post(
      `/messages/group/${groupId}`,
      data
    ),

  getCommunityMsgs: (communityId, channel) =>
    api.get(
      `/messages/community/${communityId}/${channel}`
    ),

  getCommunityMessages: (communityId, channel) =>
    api.get(
      `/messages/community/${communityId}/${channel}`
    ),

  sendCommunityMsg: (
    communityId,
    channel,
    data
  ) =>
    api.post(
      `/messages/community/${communityId}/${channel}`,
      data
    ),

  sendCommunityMessage: (
    communityId,
    channel,
    data
  ) =>
    api.post(
      `/messages/community/${communityId}/${channel}`,
      data
    ),

  deleteMessage: (id, forEveryone = false) =>
    api.delete(
      `/messages/${id}`,
      {
        data: {
          forEveryone,
        },
      }
    ),

  editMessage: (id, content) =>
    api.put(
      `/messages/${id}`,
      {
        content,
      }
    ),

  reactToMessage: (id, emoji) =>
    api.post(
      `/messages/${id}/react`,
      {
        emoji,
      }
    ),

  starMessage: (id) =>
    api.post(
      `/messages/${id}/star`
    ),

  markRead: (userId) =>
    api.post(
      `/messages/read/${userId}`
    ),

  getStarred: () =>
    api.get('/messages/starred/all'),

  getConversations: () =>
    api.get('/messages/conversations/list'),
};


/*
|--------------------------------------------------------------------------
| POSTS API
|--------------------------------------------------------------------------
*/

export const postsAPI = {

  getFeed: (page = 1) =>
    api.get(`/posts/feed?page=${page}`),

  getExplore: () =>
    api.get('/posts/explore'),

  getUserPosts: (userId) =>
    api.get(`/posts/user/${userId}`),

  create: (data) =>
    api.post('/posts', data),

  like: (id) =>
    api.post(`/posts/${id}/like`),

  repost: (id) =>
    api.post(`/posts/${id}/repost`),

  bookmark: (id) =>
    api.post(`/posts/${id}/bookmark`),

  reply: (id, data) =>
    api.post(`/posts/${id}/reply`, data),

  delete: (id) =>
    api.delete(`/posts/${id}`),
};


/*
|--------------------------------------------------------------------------
| COMMUNITIES API
|--------------------------------------------------------------------------
*/

export const communitiesAPI = {

  getAll: () =>
    api.get('/communities'),

  getMy: () =>
    api.get('/communities/my'),

  search: (q) =>
    api.get(
      `/communities/search?q=${encodeURIComponent(q)}`
    ),

  getById: (id) =>
    api.get(`/communities/${id}`),

  create: (data) =>
    api.post('/communities', data),

  join: (id) =>
    api.post(`/communities/${id}/join`),

  leave: (id) =>
    api.post(`/communities/${id}/leave`),

  addChannel: (id, data) =>
    api.post(
      `/communities/${id}/channels`,
      data
    ),
};


/*
|--------------------------------------------------------------------------
| GROUPS API
|--------------------------------------------------------------------------
*/

export const groupsAPI = {

  getAll: () =>
    api.get('/groups'),

  getById: (id) =>
    api.get(`/groups/${id}`),

  create: (data) =>
    api.post('/groups', data),

  update: (id, data) =>
    api.put(
      `/groups/${id}`,
      data
    ),

  addMember: (id, userId) =>
    api.post(
      `/groups/${id}/members`,
      {
        userId,
      }
    ),

  removeMember: (id, userId) =>
    api.delete(
      `/groups/${id}/members/${userId}`
    ),

  getMessages: (id) =>
    api.get(
      `/groups/${id}/messages`
    ),

  sendMessage: (id, data) =>
    api.post(
      `/groups/${id}/messages`,
      data
    ),

  joinByLink: (link) =>
    api.post(
      `/groups/join/${link}`
    ),
};


/*
|--------------------------------------------------------------------------
| STORIES API
|--------------------------------------------------------------------------
*/

export const storiesAPI = {

  getAll: () =>
    api.get('/stories'),

  getMine: () =>
    api.get('/stories/mine'),

  create: (data) =>
    api.post(
      '/stories',
      data
    ),

  view: (id) =>
    api.post(
      `/stories/${id}/view`
    ),

  delete: (id) =>
    api.delete(
      `/stories/${id}`
    ),
};


/*
|--------------------------------------------------------------------------
| AI API
|--------------------------------------------------------------------------
*/

export const aiAPI = {

  getSessions: () =>
    api.get('/ai/sessions'),

  getSession: (id) =>
    api.get(`/ai/sessions/${id}`),

  createSession: (data) =>
    api.post(
      '/ai/sessions',
      data
    ),

  deleteSession: (id) =>
    api.delete(
      `/ai/sessions/${id}`
    ),

  chat: (id, message) =>
    api.post(
      `/ai/sessions/${id}/chat`,
      {
        message,
      }
    ),

  runAction: (id, action) =>
    api.post(
      `/ai/sessions/${id}/action`,
      {
        action,
      }
    ),

  connectSource: (
    id,
    type,
    label
  ) =>
    api.post(
      `/ai/sessions/${id}/sources`,
      {
        type,
        label,
      }
    ),

  importContent: (
    id,
    source,
    content
  ) =>
    api.post(
      `/ai/sessions/${id}/import`,
      {
        source,
        content,
      }
    ),
};


/*
|--------------------------------------------------------------------------
| TRANSLATE API
|--------------------------------------------------------------------------
*/

export const translateAPI = {

  languages: () =>
    api.get('/translate/languages'),

  translate: (
    text,
    targetLang,
    sourceLang
  ) =>
    api.post(
      '/translate/translate',
      {
        text,
        targetLang,
        sourceLang,
      }
    ),

  detect: (text) =>
    api.post(
      '/translate/detect',
      {
        text,
      }
    ),
};


/*
|--------------------------------------------------------------------------
| SEARCH API
|--------------------------------------------------------------------------
*/

export const searchAPI = {

  search: (q, type = 'all') =>
    api.get(
      `/search?q=${encodeURIComponent(q)}&type=${type}`
    ),

  trending: () =>
    api.get('/search/trending'),
};


/*
|--------------------------------------------------------------------------
| CALLS API
|--------------------------------------------------------------------------
*/

export const callsAPI = {

  initiate: (recipientId, type) =>
    api.post(
      '/calls/initiate',
      {
        recipientId,
        type,
      }
    ),

  answer: (id) =>
    api.post(
      `/calls/${id}/answer`
    ),

  end: (id) =>
    api.post(
      `/calls/${id}/end`
    ),

  history: () =>
    api.get('/calls/history'),
};